import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Publisher / UTM Link Generator — lives inside the existing Femcare admin
 * console. Reuses the same admin JWT session (cookie + Bearer) as
 * /admin/conversions — there is NO second login and NO legacy attribution
 * bearer key ever exposed to the browser.
 *
 * UX flow:
 *  1. Admin fills the form → live preview URL updates as they type.
 *  2. Click "Generate Link" → validates client-side, freezes the preview
 *     into a "Ready to save" card WITHOUT persisting anything server-side.
 *  3. Click "Save Link" → POST to /api/web/admin/publisher-links to persist
 *     the FULL configuration (link_name, target_market, all utm_*, all sub_*,
 *     final_url, created_at, created_by). Row appears in the table below.
 *  4. Table rows expose Copy Link · View Conversions · Edit · Archive.
 *     Archive is soft (no permanent delete).
 */
async function adminFetch(path, opts = {}, token) {
    const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(API + path, { credentials: "include", ...opts, headers });
    if (!res.ok) {
        const t = await res.text().catch(() => "");
        const err = new Error(t || `HTTP ${res.status}`); err.status = res.status; throw err;
    }
    return res;
}

const DESTINATIONS = [
    { key: "home",    label: "Home",    display: "https://femcare.life/" },
    { key: "pricing", label: "Pricing", display: "https://femcare.life/pricing" },
    { key: "signup",  label: "Signup",  display: "https://femcare.life/app/signup" },
    { key: "custom",  label: "Custom Femcare URL", display: "" },
];

const MARKETS = ["", "US", "India", "UK", "Canada", "Australia", "EU", "Global"];

const EMPTY = {
    link_name: "",
    target_market: "",
    destination_kind: "home",
    destination_url: "",
    utm_source: "",
    utm_medium: "affiliate",
    utm_campaign: "femcare_web_cpa",
    utm_content: "",
    utm_term: "",
    publisher_id: "",
    pid: "",
    sub_id: "",
    sub1: "",
    sub2: "",
    sub3: "",
};

function fmtMoney(minor, ccy) {
    if (!minor) return ccy === "USD" ? "$0" : "₹0";
    const major = Number(minor) / 100;
    return ccy === "USD" ? `$${major.toFixed(2)}` : `₹${major.toFixed(major % 1 === 0 ? 0 : 2)}`;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toISOString().slice(0, 10); } catch { return String(iso).slice(0, 10); }
}

// Client-side preview builder — server is source of truth. This just lets the
// admin *see* what will be generated before they hit Generate Link.
function buildPreview(form) {
    const dest = DESTINATIONS.find(d => d.key === form.destination_kind);
    let base;
    if (form.destination_kind === "custom") {
        base = (form.destination_url || "").trim();
    } else {
        base = dest?.display || "";
    }
    if (!base) return "";
    const params = new URLSearchParams();
    const keys = ["utm_source","utm_medium","utm_campaign","utm_content","utm_term",
                  "publisher_id","pid","sub_id","sub1","sub2","sub3"];
    // Mirror utm_content from publisher_id when empty (matches server)
    const mirrored = form.utm_content || form.publisher_id;
    const eff = { ...form, utm_content: mirrored };
    keys.forEach(k => { if (eff[k]) params.set(k, eff[k].trim()); });
    try {
        const u = new URL(base);
        const existing = new URLSearchParams(u.search);
        params.forEach((v, k) => existing.set(k, v));
        u.search = existing.toString();
        return u.toString();
    } catch {
        return "";
    }
}

function validateBeforeGenerate(form) {
    for (const k of ["utm_source", "utm_medium", "utm_campaign"]) {
        if (!form[k]?.trim()) return `${k} is required`;
    }
    if (form.destination_kind === "custom") {
        const raw = form.destination_url?.trim();
        if (!raw) return "Custom destination URL required";
        if (!/^https:\/\/(www\.)?femcare\.life\b/i.test(raw))
            return "Custom URL must be on femcare.life";
    }
    if (form.utm_medium.trim().toLowerCase() === "affiliate") {
        if (!form.publisher_id?.trim() && !form.pid?.trim() && !form.utm_content?.trim())
            return "Affiliate links need at least publisher_id, pid, or utm_content";
    }
    return null;
}


// ─────────────────────────────────────────────────────────────────
// Edit dialog — opens the same field set as the generator, pre-filled
// with the row's saved values. On save, PATCHes with action=update.
// ─────────────────────────────────────────────────────────────────
function EditDialog({ open, initial, onClose, onSave, busy }) {
    const [form, setForm] = useState(initial || EMPTY);
    const [localErr, setLocalErr] = useState(null);
    const preview = useMemo(() => buildPreview(form), [form]);
    useEffect(() => { if (open) { setForm(initial || EMPTY); setLocalErr(null); } }, [open, initial]);
    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        const err = validateBeforeGenerate(form);
        if (err) { setLocalErr(err); return; }
        setLocalErr(null);
        await onSave(form);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-10 px-4"
             data-testid="pl-edit-modal">
            <div className="bg-white rounded-2xl border border-femcare-border max-w-3xl w-full p-6 shadow-xl">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-bold text-femcare-plum">Edit publisher link</h3>
                        <p className="text-xs text-femcare-ink/60 mt-1">
                            Changes regenerate the tracking URL — redistribute the new link if it's already in circulation.
                        </p>
                    </div>
                    <button type="button" onClick={onClose}
                            className="text-femcare-ink/60 hover:text-femcare-ink text-sm"
                            data-testid="pl-edit-close">Close</button>
                </div>

                {localErr && <div className="rounded-xl bg-red-50 text-red-800 px-4 py-2 text-sm mb-3"
                                  data-testid="pl-edit-error">{localErr}</div>}

                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <label className="block md:col-span-2">
                        <span className="text-xs font-semibold text-femcare-ink/70">Link Name</span>
                        <input value={form.link_name} maxLength={120}
                               onChange={(e)=>setForm(f=>({...f, link_name:e.target.value}))}
                               className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2"
                               data-testid="pl-edit-link-name" />
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-femcare-ink/70">Destination</span>
                        <select value={form.destination_kind}
                                onChange={(e)=>setForm(f=>({...f, destination_kind:e.target.value}))}
                                className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 bg-white"
                                data-testid="pl-edit-destination">
                            {DESTINATIONS.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-femcare-ink/70">Target Market</span>
                        <select value={form.target_market}
                                onChange={(e)=>setForm(f=>({...f, target_market:e.target.value}))}
                                className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 bg-white"
                                data-testid="pl-edit-market">
                            {MARKETS.map(m => <option key={m||"any"} value={m}>{m || "— any —"}</option>)}
                        </select>
                    </label>
                    {form.destination_kind === "custom" && (
                        <label className="block md:col-span-2">
                            <span className="text-xs font-semibold text-femcare-ink/70">Custom URL</span>
                            <input value={form.destination_url}
                                   onChange={(e)=>setForm(f=>({...f, destination_url:e.target.value}))}
                                   className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 font-mono text-xs"
                                   data-testid="pl-edit-custom-url" />
                        </label>
                    )}
                    {[
                        ["utm_source","Source *"],
                        ["utm_medium","Medium *"],
                        ["utm_campaign","Campaign *"],
                        ["utm_content","utm_content"],
                        ["utm_term","utm_term"],
                        ["publisher_id","Publisher ID"],
                        ["pid","PID"],
                        ["sub_id","sub_id"],
                        ["sub1","sub1"],
                        ["sub2","sub2"],
                        ["sub3","sub3"],
                    ].map(([k, lbl]) => (
                        <label key={k} className="block">
                            <span className="text-xs font-semibold text-femcare-ink/70">{lbl}</span>
                            <input value={form[k] || ""}
                                   onChange={(e)=>setForm(f=>({...f, [k]:e.target.value}))}
                                   className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-xs font-mono"
                                   data-testid={`pl-edit-${k}`} />
                        </label>
                    ))}
                    <div className="md:col-span-2 bg-femcare-cream/30 rounded-xl p-3 text-xs text-femcare-plum font-mono break-all"
                         data-testid="pl-edit-preview">
                        {preview || "Fill required fields to preview…"}
                    </div>
                    <div className="md:col-span-2 flex items-center gap-3 mt-2">
                        <button type="submit" disabled={busy}
                                className="rounded-xl bg-femcare-plum text-white text-sm font-semibold px-6 py-2.5 disabled:opacity-50"
                                data-testid="pl-edit-save">
                            {busy ? "Saving…" : "Save changes"}
                        </button>
                        <button type="button" onClick={onClose}
                                className="rounded-xl border border-femcare-border text-femcare-ink text-sm font-semibold px-5 py-2.5"
                                data-testid="pl-edit-cancel">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
}


/**
 * ClicksTab — traffic & CVR analytics for saved publisher links.
 *
 * Renders the same INR/USD revenue + Confirmed Conversions the Links tab
 * shows, plus the four click buckets (Today / 7d / 30d / Lifetime) and the
 * Last Click At timestamp so the admin can immediately spot whether a
 * publisher has started sending traffic. Sorted server-side by lifetime
 * clicks descending so busiest publishers surface first.
 *
 * IMPORTANT: The "Confirmed" and "CVR %" values shown here come from the
 * SAME `_confirmed_query()` used by /admin/conversions — a confirmed
 * conversion is STILL exclusively a Razorpay-captured payment with
 * status=entitlement_granted AND razorpay_capture_verified=true. Signups,
 * checkout-started, pending, failed, and authorized-only payments do NOT
 * count. This invariant is protected by
 * `test_confirmed_conversion_definition_unchanged` and
 * `test_cvr_uses_only_verified_captures`.
 */
function ClicksTab({ data, onRefresh, conversionsHref }) {
    if (!data) {
        return (
            <section className="bg-white rounded-2xl border border-femcare-border p-8 text-center text-femcare-ink/60"
                     data-testid="pl-clicks-loading">
                Loading clicks…
            </section>
        );
    }
    const t = data.totals || {};
    const rows = data.rows || [];
    return (
        <section className="space-y-6" data-testid="pl-clicks-section">
            {/* Overall traffic summary — the "has anyone clicked yet?" glance. */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    ["Today",    t.clicks_today, "pl-total-today"],
                    ["7 days",   t.clicks_7d,    "pl-total-7d"],
                    ["30 days",  t.clicks_30d,   "pl-total-30d"],
                    ["Lifetime", t.clicks_lifetime, "pl-total-lifetime"],
                ].map(([label, val, tid]) => (
                    <div key={label} className="rounded-2xl bg-white border border-femcare-border p-5" data-testid={tid}>
                        <div className="text-xs uppercase tracking-wider text-femcare-ink/55">{label}</div>
                        <div className="text-3xl font-bold text-femcare-plum mt-2">{val ?? 0}</div>
                        <div className="text-xs text-femcare-ink/45 mt-1">clicks</div>
                    </div>
                ))}
            </div>

            <div className="bg-white rounded-2xl border border-femcare-border">
                <div className="flex items-center justify-between px-6 py-4 border-b border-femcare-border">
                    <div>
                        <h2 className="text-sm font-semibold uppercase tracking-widest text-femcare-ink/60">Per-Publisher Traffic</h2>
                        <p className="text-xs text-femcare-ink/50 mt-0.5">
                            CVR = Confirmed Conversions ÷ Lifetime Clicks × 100. A Confirmed Conversion is a Razorpay-captured payment only.
                        </p>
                    </div>
                    <button type="button" onClick={onRefresh}
                        className="text-xs font-semibold text-femcare-plum hover:underline"
                        data-testid="pl-clicks-refresh">
                        Refresh
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="pl-clicks-table">
                        <thead className="bg-femcare-cream/40 text-femcare-ink/70">
                            <tr className="text-left text-xs uppercase tracking-wider">
                                <th className="px-4 py-3">Publisher / Link Name</th>
                                <th className="px-4 py-3">Today</th>
                                <th className="px-4 py-3">7d</th>
                                <th className="px-4 py-3">30d</th>
                                <th className="px-4 py-3">Lifetime</th>
                                <th className="px-4 py-3">Last Click At</th>
                                <th className="px-4 py-3">Confirmed</th>
                                <th className="px-4 py-3">CVR %</th>
                                <th className="px-4 py-3">INR Revenue</th>
                                <th className="px-4 py-3">USD Revenue</th>
                                <th className="px-4 py-3">View</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr><td colSpan={11} className="px-4 py-10 text-center text-femcare-ink/50" data-testid="pl-clicks-empty">
                                    No traffic yet — clicks will appear here as visitors land on femcare.life via your saved links.
                                </td></tr>
                            )}
                            {rows.map((r) => (
                                <tr key={r.id} className="border-t border-femcare-border" data-testid={`pl-clicks-row-${r.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold text-femcare-plum">{r.link_name || r.utm_source || "—"}</div>
                                        <div className="text-[10px] text-femcare-ink/55 font-mono">
                                            {r.utm_source}{r.publisher_id ? ` · ${r.publisher_id}` : ""}{r.pid ? ` · ${r.pid}` : ""}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 font-semibold" data-testid={`pl-c-today-${r.id}`}>{r.clicks_today ?? 0}</td>
                                    <td className="px-4 py-3" data-testid={`pl-c-7d-${r.id}`}>{r.clicks_7d ?? 0}</td>
                                    <td className="px-4 py-3" data-testid={`pl-c-30d-${r.id}`}>{r.clicks_30d ?? 0}</td>
                                    <td className="px-4 py-3 font-semibold" data-testid={`pl-c-life-${r.id}`}>{r.clicks_lifetime ?? 0}</td>
                                    <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`pl-c-last-${r.id}`}>
                                        {r.last_click_at ? fmtDate(r.last_click_at) : <span className="text-femcare-ink/40">never</span>}
                                    </td>
                                    <td className="px-4 py-3 font-semibold" data-testid={`pl-c-conf-${r.id}`}>{r.confirmed_conversions ?? 0}</td>
                                    <td className="px-4 py-3 font-semibold text-femcare-plum" data-testid={`pl-c-cvr-${r.id}`}>
                                        {(r.cvr_pct ?? 0).toFixed(2)}%
                                    </td>
                                    <td className="px-4 py-3 text-xs">{fmtMoney(r.inr_minor || 0, "INR")}</td>
                                    <td className="px-4 py-3 text-xs">{fmtMoney(r.usd_minor || 0, "USD")}</td>
                                    <td className="px-4 py-3">
                                        <Link to={conversionsHref(r)}
                                            className="text-xs font-semibold text-femcare-plum hover:underline"
                                            data-testid={`pl-c-view-${r.id}`}>
                                            Conversions
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}




export default function AdminPublisherLinks() {
    const { user } = useAuth();
    const accessToken = typeof window !== "undefined" ? localStorage.getItem("femcare_token") : null;
    const navigate = useNavigate();

    useEffect(() => {
        if (user === false) navigate("/admin/login", { replace: true });
        else if (user && user.role && user.role !== "admin") navigate("/", { replace: true });
    }, [user, navigate]);

    const [form, setForm] = useState(EMPTY);
    const [rows, setRows] = useState([]);
    const [includeArchived, setIncludeArchived] = useState(false);
    const [err, setErr] = useState(null);
    const [saveMsg, setSaveMsg] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [busy, setBusy] = useState(false);

    // "Generate Link" produces a frozen preview + unlocks the Save button.
    // Kept separate from the live typing preview so admins have a clear
    // review-before-persist checkpoint — BUT both buttons are visible from
    // the start (Save is enabled the moment required fields are filled) so
    // admins never have to hunt for the Save action.
    const [generated, setGenerated] = useState(null);
    const [editRow, setEditRow] = useState(null);
    const [editBusy, setEditBusy] = useState(false);

    // Tabs: "links" (default) or "clicks" (traffic + CVR analytics).
    const [tab, setTab] = useState("links");
    const [clicksSummary, setClicksSummary] = useState(null);

    // Publishers map keyed by `publisher_id` — used to (a) short-circuit the
    // "Create Account" button when a link's publisher_id already has an
    // account provisioned, and (b) show the account name inline on the row.
    // Loaded once alongside links; refreshed after every create.
    const [publishersByPubId, setPublishersByPubId] = useState({});
    const [createAcctRow,    setCreateAcctRow]    = useState(null);
    const [acctSuccess,      setAcctSuccess]      = useState(null);

    const livePreviewUrl = useMemo(() => buildPreview(form), [form]);

    const load = async () => {
        if (!user || user.role !== "admin") return;
        setErr(null);
        try {
            const r = await adminFetch(
                `/api/web/admin/publisher-links${includeArchived ? "?include_archived=true" : ""}`,
                {}, accessToken
            );
            const data = await r.json();
            setRows(data.rows || []);
        } catch (e) {
            setErr(e.message);
            if (e.status === 401) navigate("/admin/login", { replace: true });
        }
        // Load publishers so we can annotate rows with "account exists".
        try {
            const rp = await adminFetch("/api/web/admin/publishers", {}, accessToken);
            const pd = await rp.json();
            const map = {};
            for (const p of (pd.rows || [])) {
                for (const pid of (p.publisher_ids || [])) map[pid] = p;
            }
            setPublishersByPubId(map);
        } catch (_) { /* non-fatal — Create Account still works, just no gating */ }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [user, includeArchived]);

    // Load the aggregate Clicks tab data lazily — only when the admin opens
    // the tab, and refreshes on every tab switch so numbers stay live.
    const loadClicks = async () => {
        if (!user || user.role !== "admin") return;
        try {
            const r = await adminFetch("/api/web/admin/clicks/summary", {}, accessToken);
            setClicksSummary(await r.json());
        } catch (e) {
            setErr(e.message);
        }
    };
    useEffect(() => { if (tab === "clicks") loadClicks(); /* eslint-disable-next-line */ }, [tab, user]);

    // Combined Generate → Save flow. When the admin clicks Save without
    // having clicked Generate first, we synthesise the Generate step
    // silently so the UX stays "one click == save". This is what fixes the
    // "Save Link button is not available" report — the button is now
    // always visible and always actionable when required fields are filled.
    const doSaveOrGenerateSave = async (mode) => {
        setErr(null); setSaveMsg(null);
        const v = validateBeforeGenerate(form);
        if (v) { setErr(v); return; }
        // Freeze the preview so the "Ready to save" card also reflects the
        // exact URL that will be persisted, then persist.
        const g = { url: livePreviewUrl, form: { ...form } };
        setGenerated(g);
        if (mode === "generate-only") return;
        setBusy(true);
        try {
            const r = await adminFetch("/api/web/admin/publisher-links", {
                method: "POST",
                body: JSON.stringify(g.form),
            }, accessToken);
            const data = await r.json();
            setSaveMsg(`Saved · ${data.link?.link_name || data.link?.id || "link"}`);
            try { await navigator.clipboard?.writeText(data.link.final_url); } catch { /* ignore */ }
            setForm(EMPTY);
            setGenerated(null);
            await load();
        } catch (e) {
            setErr(e.message);
        } finally { setBusy(false); }
    };

    // Legacy handlers kept for the "Ready to save" card action buttons.
    const doGenerate = (e) => { e?.preventDefault?.(); doSaveOrGenerateSave("generate-only"); };
    const doSave     = () => doSaveOrGenerateSave("save");

    // Create a publisher account bound to THIS link's publisher_id so the
    // publisher automatically inherits every link that shares the id
    // (auto-mapping — matches test_publisher_panel::test_A/B/C). The button
    // short-circuits to "Account exists" when a publisher already owns this
    // publisher_id, so admins can't accidentally create a duplicate.
    const doCreateAccount = async (form) => {
        setErr(null); setAcctSuccess(null);
        try {
            const r = await adminFetch("/api/web/admin/publishers", {
                method: "POST",
                body: JSON.stringify(form),
            }, accessToken);
            const data = await r.json();
            setCreateAcctRow(null);
            setAcctSuccess({
                email:    data.publisher?.email || form.email,
                password: data.temporary_password,           // server-generated
                name:     data.publisher?.display_name || "",
            });
            await load();
        } catch (e) { setErr(e.message); }
    };

    const copyLink = async (id, url) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiedId(id);
            setTimeout(() => setCopiedId((v) => (v === id ? null : v)), 1600);
        } catch { setErr("Copy failed"); }
    };

    const toggleArchive = async (link) => {
        try {
            await adminFetch(`/api/web/admin/publisher-links/${link.id}`, {
                method: "PATCH",
                body: JSON.stringify({ action: link.archived ? "restore" : "archive" }),
            }, accessToken);
            await load();
        } catch (e) { setErr(e.message); }
    };

    const submitEdit = async (fields) => {
        if (!editRow) return;
        setEditBusy(true); setErr(null);
        try {
            await adminFetch(`/api/web/admin/publisher-links/${editRow.id}`, {
                method: "PATCH",
                body: JSON.stringify({ action: "update", fields }),
            }, accessToken);
            setEditRow(null);
            setSaveMsg("Link updated");
            await load();
        } catch (e) {
            setErr(e.message);
        } finally { setEditBusy(false); }
    };

    const conversionsHref = (link) => {
        const pub = link.publisher_id || link.pid || link.utm_content;
        return pub ? `/admin/conversions?publisher_id=${encodeURIComponent(pub)}` : "/admin/conversions";
    };

    if (!user) return <div className="p-12 text-femcare-ink/60">Loading…</div>;

    return (
        <div className="min-h-screen bg-femcare-cream/40" data-testid="admin-publisher-links-page">
            <header className="bg-white border-b border-femcare-border">
                <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
                    <div className="flex items-center gap-6">
                        <Link to="/admin" className="font-serif text-xl text-femcare-plum">Femcare Admin</Link>
                        <nav className="flex gap-4 text-sm text-femcare-ink/70">
                            <Link to="/admin" data-testid="admin-nav-dashboard">Dashboard</Link>
                            <Link to="/admin/conversions" data-testid="admin-nav-conversions">Web Conversions</Link>
                            <Link to="/admin/publisher-links" className="text-femcare-plum font-semibold" data-testid="admin-nav-publisher-links">Publisher Links</Link>
                        </nav>
                    </div>
                    <span className="text-xs text-femcare-ink/60">{user?.email}</span>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
                <div>
                    <h1 className="text-2xl font-bold text-femcare-plum">Publisher Links</h1>
                    <p className="text-sm text-femcare-ink/65 mt-1">Create trackable links for affiliates, publishers and marketing campaigns.</p>
                </div>

                {err && <div className="rounded-xl bg-red-50 text-red-800 px-4 py-3 text-sm" data-testid="pl-error">{err}</div>}
                {saveMsg && <div className="rounded-xl bg-emerald-50 text-emerald-800 px-4 py-3 text-sm" data-testid="pl-save-msg">{saveMsg}</div>}

                {/* ============ GENERATOR FORM ============ */}
                <section className="bg-white rounded-2xl border border-femcare-border p-6">
                    <h2 className="text-sm font-semibold uppercase tracking-widest text-femcare-ink/60 mb-4">Generate a link</h2>
                    <form onSubmit={(e)=>{e.preventDefault(); doSave();}} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pl-form">

                        <label className="block md:col-span-2">
                            <span className="text-xs font-semibold text-femcare-ink/70">Link Name <em className="not-italic text-femcare-ink/50">(admin organisation only)</em></span>
                            <input type="text" maxLength={120} value={form.link_name}
                                onChange={(e)=>setForm(f=>({...f, link_name:e.target.value}))}
                                placeholder="Awin US September"
                                className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-sm"
                                data-testid="pl-link-name" />
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-femcare-ink/70">Destination *</span>
                            <select value={form.destination_kind}
                                onChange={(e)=>setForm(f=>({...f, destination_kind:e.target.value}))}
                                className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-sm bg-white"
                                data-testid="pl-destination">
                                {DESTINATIONS.map(d => <option key={d.key} value={d.key}>{d.label}{d.display ? ` — ${d.display}` : ""}</option>)}
                            </select>
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-femcare-ink/70">Target Market <em className="not-italic text-femcare-ink/50">(organisation only)</em></span>
                            <select value={form.target_market}
                                onChange={(e)=>setForm(f=>({...f, target_market:e.target.value}))}
                                className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-sm bg-white"
                                data-testid="pl-market">
                                {MARKETS.map(m => <option key={m||"any"} value={m}>{m || "— any —"}</option>)}
                            </select>
                        </label>

                        {form.destination_kind === "custom" && (
                            <label className="block md:col-span-2">
                                <span className="text-xs font-semibold text-femcare-ink/70">Custom URL * <em className="not-italic text-femcare-ink/50">(femcare.life only)</em></span>
                                <input type="url" value={form.destination_url}
                                    onChange={(e)=>setForm(f=>({...f, destination_url:e.target.value}))}
                                    placeholder="https://femcare.life/features/cycle"
                                    className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-sm font-mono"
                                    data-testid="pl-custom-url" />
                            </label>
                        )}

                        <label className="block">
                            <span className="text-xs font-semibold text-femcare-ink/70">Network / utm_source *</span>
                            <input type="text" list="pl-source-list" value={form.utm_source}
                                onChange={(e)=>setForm(f=>({...f, utm_source:e.target.value}))}
                                placeholder="awin"
                                className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-sm"
                                data-testid="pl-utm-source" required />
                            <datalist id="pl-source-list">
                                {["awin","impact","youappi","mobidea","publisher_direct","cj","rakuten","tradedoubler"].map(s => <option key={s} value={s} />)}
                            </datalist>
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-femcare-ink/70">Medium / utm_medium *</span>
                            <input type="text" value={form.utm_medium}
                                onChange={(e)=>setForm(f=>({...f, utm_medium:e.target.value}))}
                                className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-sm"
                                data-testid="pl-utm-medium" required />
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-femcare-ink/70">Campaign / utm_campaign *</span>
                            <input type="text" value={form.utm_campaign}
                                onChange={(e)=>setForm(f=>({...f, utm_campaign:e.target.value}))}
                                className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-sm"
                                data-testid="pl-utm-campaign" required />
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-femcare-ink/70">Publisher ID</span>
                            <input type="text" value={form.publisher_id}
                                onChange={(e)=>setForm(f=>({...f, publisher_id:e.target.value}))}
                                placeholder="publisher123"
                                className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-sm font-mono"
                                data-testid="pl-publisher-id" />
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-femcare-ink/70">PID <em className="not-italic text-femcare-ink/50">(optional)</em></span>
                            <input type="text" value={form.pid}
                                onChange={(e)=>setForm(f=>({...f, pid:e.target.value}))}
                                className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-sm font-mono"
                                data-testid="pl-pid" />
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-femcare-ink/70">utm_content <em className="not-italic text-femcare-ink/50">(auto: publisher_id if blank)</em></span>
                            <input type="text" value={form.utm_content}
                                onChange={(e)=>setForm(f=>({...f, utm_content:e.target.value}))}
                                className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-sm"
                                data-testid="pl-utm-content" />
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-femcare-ink/70">utm_term <em className="not-italic text-femcare-ink/50">(optional)</em></span>
                            <input type="text" value={form.utm_term}
                                onChange={(e)=>setForm(f=>({...f, utm_term:e.target.value}))}
                                className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-sm"
                                data-testid="pl-utm-term" />
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-femcare-ink/70">sub_id</span>
                            <input type="text" value={form.sub_id}
                                onChange={(e)=>setForm(f=>({...f, sub_id:e.target.value}))}
                                className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-sm font-mono"
                                data-testid="pl-sub-id" />
                        </label>

                        <div className="grid grid-cols-3 gap-3 md:col-span-2">
                            {["sub1","sub2","sub3"].map(k => (
                                <label key={k} className="block">
                                    <span className="text-xs font-semibold text-femcare-ink/70">{k}</span>
                                    <input type="text" value={form[k]}
                                        onChange={(e)=>setForm(f=>({...f, [k]:e.target.value}))}
                                        className="mt-1 w-full rounded-lg border border-femcare-border px-3 py-2 text-sm font-mono"
                                        data-testid={`pl-${k}`} />
                                </label>
                            ))}
                        </div>

                        {/* Live preview (updates as user types) */}
                        <div className="md:col-span-2 bg-femcare-cream/30 rounded-xl p-4" data-testid="pl-preview">
                            <div className="text-xs uppercase tracking-wider font-semibold text-femcare-ink/60 mb-2">Live preview</div>
                            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs text-femcare-ink/75 mb-3">
                                <div><b className="block text-femcare-ink/55">Source</b>{form.utm_source || "—"}</div>
                                <div><b className="block text-femcare-ink/55">Medium</b>{form.utm_medium || "—"}</div>
                                <div><b className="block text-femcare-ink/55">Campaign</b>{form.utm_campaign || "—"}</div>
                                <div><b className="block text-femcare-ink/55">Publisher</b>{form.publisher_id || "—"}</div>
                                <div><b className="block text-femcare-ink/55">PID</b>{form.pid || "—"}</div>
                                <div><b className="block text-femcare-ink/55">Destination</b>{form.destination_kind}</div>
                            </div>
                            <div className="bg-white border border-femcare-border rounded-lg p-3 text-xs font-mono break-all text-femcare-plum" data-testid="pl-preview-url">
                                {livePreviewUrl || "Fill in the required fields to preview…"}
                            </div>
                        </div>

                        <div className="md:col-span-2 flex flex-wrap items-center gap-3 mt-1">
                            {/*
                             * Single primary action at the form level — Generate Link.
                             * After clicking, the "Ready to save" panel below owns the
                             * Save + Copy + Discard actions so each control lives in
                             * exactly one place and there is no duplicate CTA.
                             */}
                            <button type="button" onClick={doGenerate}
                                    disabled={!!validateBeforeGenerate(form)}
                                    className="rounded-xl bg-femcare-plum text-white text-sm font-semibold px-6 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="pl-generate">
                                Generate Link
                            </button>
                        </div>
                    </form>

                    {/* Frozen "Ready to save" card, appears only after Generate */}
                    {generated && (
                        <div className="mt-6 rounded-2xl border-2 border-femcare-plum/30 bg-femcare-cream/30 p-5"
                             data-testid="pl-ready-to-save">
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs uppercase tracking-wider font-semibold text-femcare-plum mb-1">
                                        Ready to save
                                    </div>
                                    <div className="text-sm text-femcare-ink/70 mb-3">
                                        Review the generated URL below, then click <b>Save Link</b> to persist the full
                                        configuration server-side so it appears in the tracking table.
                                    </div>
                                    <div className="bg-white border border-femcare-border rounded-lg p-3 text-xs font-mono break-all text-femcare-plum"
                                         data-testid="pl-generated-url">
                                        {generated.url}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 flex flex-wrap items-center gap-3">
                                {/*
                                 * PANEL Save Link — the primary CTA inside the "Ready to save"
                                 * card. Unique testid `pl-save-panel` so it never collides
                                 * with the always-visible form-level Save button (`pl-save`)
                                 * above. Both buttons call the same doSave handler and are
                                 * functionally equivalent — this one is here so the panel is
                                 * a self-contained review-and-confirm surface (Save · Copy ·
                                 * Discard) without forcing the admin's eye back up to the form.
                                 */}
                                <button type="button" onClick={doSave} disabled={busy}
                                        className="rounded-xl bg-femcare-plum text-white text-sm font-semibold px-6 py-2.5 disabled:opacity-50 shadow-sm"
                                        data-testid="pl-save-panel">
                                    {busy ? "Saving…" : "Save Link"}
                                </button>
                                <button type="button"
                                        onClick={()=>copyLink("__generated__", generated.url)}
                                        className="rounded-xl border border-femcare-border text-femcare-plum text-sm font-semibold px-5 py-2.5"
                                        data-testid="pl-copy-generated">
                                    {copiedId === "__generated__" ? "Link copied" : "Copy Link"}
                                </button>
                                <button type="button" onClick={()=>setGenerated(null)}
                                        className="text-xs text-femcare-ink/60 hover:text-femcare-ink"
                                        data-testid="pl-discard">
                                    Discard & edit
                                </button>
                            </div>
                        </div>
                    )}
                </section>

                {/* ============ TABS ============ */}
                <div className="flex items-center gap-1 border-b border-femcare-border">
                    <button type="button"
                        onClick={()=>setTab("links")}
                        className={`px-5 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab==="links" ? "border-femcare-plum text-femcare-plum" : "border-transparent text-femcare-ink/60 hover:text-femcare-ink"}`}
                        data-testid="pl-tab-links">
                        Saved Links
                    </button>
                    <button type="button"
                        onClick={()=>setTab("clicks")}
                        className={`px-5 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab==="clicks" ? "border-femcare-plum text-femcare-plum" : "border-transparent text-femcare-ink/60 hover:text-femcare-ink"}`}
                        data-testid="pl-tab-clicks">
                        Clicks & Traffic
                    </button>
                </div>

                {tab === "clicks" && (
                    <ClicksTab data={clicksSummary} onRefresh={loadClicks} conversionsHref={conversionsHref} />
                )}

                {tab === "links" && (
                <section className="bg-white rounded-2xl border border-femcare-border" data-testid="pl-links-section">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-femcare-border">
                        <div>
                            <h2 className="text-sm font-semibold uppercase tracking-widest text-femcare-ink/60">Saved Publisher Links</h2>
                            <p className="text-xs text-femcare-ink/50 mt-0.5">
                                Clicks & CVR come from the passive click beacon (deduped per browser session).
                                Confirmed Conversions come from captured Razorpay payments only.
                            </p>
                        </div>
                        <label className="text-xs text-femcare-ink/70 flex items-center gap-2">
                            <input type="checkbox" checked={includeArchived} onChange={(e)=>setIncludeArchived(e.target.checked)} data-testid="pl-toggle-archived" />
                            Show archived
                        </label>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="pl-table">
                            <thead className="bg-femcare-cream/40 text-femcare-ink/70">
                                <tr className="text-left text-xs uppercase tracking-wider">
                                    <th className="px-4 py-3">Created</th>
                                    <th className="px-4 py-3">Link Name</th>
                                    <th className="px-4 py-3">Publisher</th>
                                    <th className="px-4 py-3">Source</th>
                                    <th className="px-4 py-3">Campaign</th>
                                    <th className="px-4 py-3">Target Market</th>
                                    <th className="px-4 py-3">Publisher ID</th>
                                    <th className="px-4 py-3">PID</th>
                                    <th className="px-4 py-3">Tracking URL</th>
                                    <th className="px-4 py-3">Clicks</th>
                                    <th className="px-4 py-3">CVR %</th>
                                    <th className="px-4 py-3">Confirmed</th>
                                    <th className="px-4 py-3">INR Revenue</th>
                                    <th className="px-4 py-3">USD Revenue</th>
                                    <th className="px-4 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr><td colSpan={15} className="px-4 py-10 text-center text-femcare-ink/50">No links yet — generate your first one above.</td></tr>
                                )}
                                {rows.map((r) => (
                                    <tr key={r.id} className={`border-t border-femcare-border ${r.archived ? "opacity-50" : ""}`} data-testid={`pl-row-${r.id}`}>
                                        <td className="px-4 py-3 text-xs text-femcare-ink/70 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="font-semibold text-femcare-plum">{r.link_name || "—"}</div>
                                            {r.updated_at && <div className="text-[10px] text-femcare-ink/45">edited {fmtDate(r.updated_at)}</div>}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">{r.link_name || r.utm_source || "—"}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">{r.utm_source}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">{r.utm_campaign}</td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs">{r.target_market || "—"}</td>
                                        <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{r.publisher_id || "—"}</td>
                                        <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{r.pid || "—"}</td>
                                        <td className="px-4 py-3 max-w-[260px]">
                                            <div className="text-xs font-mono truncate text-femcare-ink/70" title={r.final_url}>
                                                {r.final_url}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap" data-testid={`pl-clicks-${r.id}`}>
                                            <div className="font-semibold">{r.performance?.clicks_lifetime || 0}</div>
                                            {r.performance?.last_click_at && (
                                                <div className="text-[10px] text-femcare-ink/50" title={r.performance.last_click_at}>
                                                    last {fmtDate(r.performance.last_click_at)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap font-semibold text-femcare-plum" data-testid={`pl-cvr-${r.id}`}>
                                            {(r.performance?.cvr_pct || 0).toFixed(2)}%
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap font-semibold" data-testid={`pl-conf-${r.id}`}>{r.performance?.confirmed_conversions || 0}</td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs" data-testid={`pl-inr-${r.id}`}>{fmtMoney(r.performance?.inr_minor || 0, "INR")}</td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs" data-testid={`pl-usd-${r.id}`}>{fmtMoney(r.performance?.usd_minor || 0, "USD")}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                <button type="button"
                                                    onClick={()=>copyLink(r.id, r.final_url)}
                                                    className="text-xs font-semibold text-femcare-plum hover:underline"
                                                    data-testid={`pl-copy-${r.id}`}>
                                                    {copiedId === r.id ? "Copied" : "Copy"}
                                                </button>
                                                <Link to={conversionsHref(r)}
                                                    className="text-xs font-semibold text-femcare-plum hover:underline"
                                                    data-testid={`pl-view-conv-${r.id}`}>
                                                    Conversions
                                                </Link>
                                                <button type="button"
                                                    onClick={()=>setEditRow(r)}
                                                    className="text-xs font-semibold text-femcare-plum hover:underline"
                                                    data-testid={`pl-edit-${r.id}`}
                                                    disabled={r.archived}>
                                                    Edit
                                                </button>
                                                {/* Create Account — bind a publisher account to this link's
                                                  * publisher_id so they can log into /publisher and see their own
                                                  * traffic + confirmed conversions. Short-circuits to "Account: <name>"
                                                  * (link to /admin/publishers) when a publisher already owns this
                                                  * publisher_id, preventing duplicate account creation. */}
                                                {r.publisher_id && publishersByPubId[r.publisher_id] ? (
                                                    (() => {
                                                        // Account exists — show a small activity badge so admins
                                                        // spot at a glance which affiliates are engaged vs quiet.
                                                        // "active" = logged in within the last 7 days.
                                                        const p = publishersByPubId[r.publisher_id];
                                                        const last = p.last_login_at;
                                                        const lastMs = last ? new Date(last).getTime() : 0;
                                                        const daysAgo = lastMs ? Math.floor((Date.now() - lastMs) / 86400000) : null;
                                                        let badge = "never";
                                                        let tone  = "text-femcare-ink/50 bg-femcare-ink/5";
                                                        if (daysAgo !== null) {
                                                            if (daysAgo <= 7)      { badge = daysAgo === 0 ? "today"    : `${daysAgo}d`; tone = "text-emerald-800 bg-emerald-50"; }
                                                            else if (daysAgo <= 30){ badge = `${daysAgo}d`;                              tone = "text-amber-800   bg-amber-50";   }
                                                            else                   { badge = `${daysAgo}d`;                              tone = "text-red-700     bg-red-50";     }
                                                        }
                                                        return (
                                                            <Link to="/admin/publishers"
                                                                className="flex items-center gap-1"
                                                                title={`Publisher: ${p.display_name || p.email}${last ? ` · last login ${new Date(last).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" })} IST` : " · never logged in"}`}
                                                                data-testid={`pl-account-exists-${r.id}`}>
                                                                <span className="text-xs font-semibold text-emerald-700 hover:underline">Account ✓</span>
                                                                <span className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${tone}`}
                                                                      data-testid={`pl-account-last-${r.id}`}>
                                                                    {badge}
                                                                </span>
                                                            </Link>
                                                        );
                                                    })()
                                                ) : (
                                                    <button type="button"
                                                        onClick={()=>{ setAcctSuccess(null); setCreateAcctRow(r); }}
                                                        className="text-xs font-semibold text-femcare-plum hover:underline"
                                                        data-testid={`pl-create-account-${r.id}`}
                                                        disabled={r.archived}>
                                                        Create Account
                                                    </button>
                                                )}
                                                <button type="button"
                                                    onClick={()=>toggleArchive(r)}
                                                    className="text-xs font-semibold text-femcare-ink/60 hover:text-red-700"
                                                    data-testid={`pl-archive-${r.id}`}>
                                                    {r.archived ? "Restore" : "Archive"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
                )}
            </main>

            <EditDialog
                open={!!editRow}
                initial={editRow}
                onClose={()=>setEditRow(null)}
                onSave={submitEdit}
                busy={editBusy}
            />

            {createAcctRow && (
                <CreateAccountDialog
                    row={createAcctRow}
                    onClose={()=>setCreateAcctRow(null)}
                    onSave={doCreateAccount}
                />
            )}

            {acctSuccess && (
                <div className="fixed bottom-6 right-6 max-w-md rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 p-5 shadow-lg z-50"
                     data-testid="pl-account-created-banner">
                    <div className="text-sm font-semibold mb-1">Publisher account created</div>
                    <div className="text-xs mb-3">
                        Share these credentials once — Femcare does NOT store the plaintext password.
                        Publisher signs in at <span className="font-mono">/publisher/login</span>.
                    </div>
                    <div className="rounded-lg bg-white border border-amber-300 p-3 text-xs font-mono break-all">
                        <div><span className="text-amber-700">email:</span> {acctSuccess.email}</div>
                        <div><span className="text-amber-700">password:</span> {acctSuccess.password}</div>
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                        <button
                            onClick={()=>{ navigator.clipboard?.writeText(`${acctSuccess.email} / ${acctSuccess.password}`); }}
                            className="text-xs font-semibold text-femcare-plum hover:underline"
                            data-testid="pl-account-created-copy">
                            Copy credentials
                        </button>
                        <button
                            onClick={()=>setAcctSuccess(null)}
                            className="text-xs font-semibold text-femcare-ink/60 hover:text-red-700"
                            data-testid="pl-account-created-dismiss">
                            Dismiss
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}


/**
 * CreateAccountDialog — inline "provision a publisher for THIS link" flow.
 * Pre-fills publisher_ids with the row's `publisher_id` so auto-mapping
 * kicks in immediately (every existing + future link with that id becomes
 * visible on the new publisher's dashboard). Admin can still add more
 * publisher_ids via commas if the same publisher owns multiple sub-accounts.
 */
function CreateAccountDialog({ row, onClose, onSave }) {
    const suggestedIds = row?.publisher_id
        ? row.publisher_id
        : (row?.pid || row?.utm_content || "");
    const [form, setForm] = useState({
        email:         "",
        display_name:  row?.link_name || row?.utm_source || "",
        publisher_ids: suggestedIds,
    });
    const [busy, setBusy] = useState(false);
    const [localErr, setLocalErr] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setLocalErr(null);
        if (!form.email || !form.email.includes("@")) { setLocalErr("Enter a valid email"); return; }
        if (!form.publisher_ids.trim())               { setLocalErr("At least one publisher_id is required so the account auto-maps to links"); return; }
        setBusy(true);
        try {
            // NOTE: no password field is sent. The server generates the
            // publisher's initial password and returns it once in the
            // response. This means an admin can never mint a login they
            // know for the publisher — only reset it later.
            await onSave({
                email:         form.email.trim().toLowerCase(),
                display_name:  form.display_name.trim(),
                publisher_ids: form.publisher_ids.split(",").map(s=>s.trim()).filter(Boolean),
                active:        true,
            });
        } catch (e2) {
            setLocalErr(e2.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="pl-create-account-modal">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
                <div className="flex items-start justify-between">
                    <div>
                        <h2 className="text-lg font-bold text-femcare-plum">Create Publisher Account</h2>
                        <p className="text-xs text-femcare-ink/60 mt-1">
                            For link <span className="font-semibold">{row?.link_name || row?.utm_source}</span>
                            {row?.publisher_id ? <> · publisher_id <span className="font-mono">{row.publisher_id}</span></> : null}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-xs text-femcare-ink/50 hover:text-red-700" data-testid="pl-ca-close">Close</button>
                </div>

                {localErr && <div className="rounded-xl bg-red-50 text-red-800 px-3 py-2 text-sm" data-testid="pl-ca-error">{localErr}</div>}

                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <label className="block text-xs uppercase tracking-wider text-femcare-ink/60 mb-1">Email</label>
                        <input type="email" required autoFocus value={form.email}
                               onChange={(e)=>setForm({...form, email:e.target.value})}
                               className="w-full rounded-xl border border-femcare-border px-3 py-2 text-sm focus:outline-none focus:border-femcare-plum"
                               data-testid="pl-ca-email" />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-wider text-femcare-ink/60 mb-1">Display Name</label>
                        <input type="text" value={form.display_name}
                               onChange={(e)=>setForm({...form, display_name:e.target.value})}
                               className="w-full rounded-xl border border-femcare-border px-3 py-2 text-sm focus:outline-none focus:border-femcare-plum"
                               data-testid="pl-ca-name" />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-wider text-femcare-ink/60 mb-1">
                            Publisher IDs <span className="text-femcare-ink/40 normal-case">(comma-separated · auto-maps every matching link)</span>
                        </label>
                        <input type="text" value={form.publisher_ids}
                               onChange={(e)=>setForm({...form, publisher_ids:e.target.value})}
                               className="w-full rounded-xl border border-femcare-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-femcare-plum"
                               data-testid="pl-ca-ids" />
                    </div>
                    <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3">
                        Femcare generates a secure random password server-side. It appears once in the confirmation
                        below and is never stored in plaintext. Admins cannot choose or view the password —
                        only the publisher receives it, and only reset (never set) is possible afterwards.
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                        <button type="submit" disabled={busy}
                                className="rounded-xl bg-femcare-plum text-white text-sm font-semibold px-5 py-2 disabled:opacity-40"
                                data-testid="pl-ca-submit">
                            {busy ? "Creating…" : "Create Account"}
                        </button>
                        <button type="button" onClick={onClose}
                                className="text-xs font-semibold text-femcare-ink/60 hover:text-red-700"
                                data-testid="pl-ca-cancel">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
